import React from 'react';
import PropTypes from 'prop-types';
import {
  Table, Header, Message, Icon,
} from 'semantic-ui-react';

import ListRow from './ListRow';
import {
  MASTER_TYPE,
  PARTY_SIDE_LABEL,
  groupByPayerSide,
  effectiveMasterFor,
  scheduledMasterFor,
} from '../../constants/defaultCoverageMaster';

const headerRow = (
  <Table.Row>
    <Table.HeaderCell>Product</Table.HeaderCell>
    <Table.HeaderCell>Price</Table.HeaderCell>
    <Table.HeaderCell>Started</Table.HeaderCell>
    <Table.HeaderCell>Ended</Table.HeaderCell>
    <Table.HeaderCell>Status</Table.HeaderCell>
    <Table.HeaderCell />
  </Table.Row>
);

// One section per payer side. The "receives" line states the outcome the table
// implies but does not say out loud: with no opt-outs, this is the product a
// party on this side actually gets. No priority any more (v2) — a side
// realistically holds one master.
const SideSection = ({ masterType, masters, actionHandler }) => {
  const effective = effectiveMasterFor(masters);
  const scheduled = effective ? null : scheduledMasterFor(masters);

  return (
    <div style={{ marginBottom: '2em' }}>
      <Header
        size='small'
        data-testid={`default-coverage-masters-side-${masterType}`}
      >
        {PARTY_SIDE_LABEL[masterType]}
      </Header>

      <Message
        size='tiny'
        info={Boolean(effective)}
        warning={!effective}
        data-testid={`default-coverage-masters-effective-${masterType}`}
      >
        {effective && (
          <span>
            A party on this side with no opt-outs receives <b>{effective.productCode}</b>.
          </span>
        )}
        {!effective && scheduled && (
          <span>
            No master resolves on this side yet. <b>{scheduled.productCode}</b> is
            scheduled to start on {String(scheduled.startTs).slice(0, 10)}; until
            then no sale receives a product by default here.
          </span>
        )}
        {!effective && !scheduled && (
          <span>
            No master resolves on this side, so no sale receives a product by
            default here.
          </span>
        )}
      </Message>

      {masters.length > 0 && (
        <Table
          celled
          padded
          renderBodyRow={ListRow(actionHandler)}
          tableData={masters}
          headerRow={headerRow}
          size='tiny'
          compact='very'
          style={{ fontSize: '90%' }}
          striped
        />
      )}
    </div>
  );
};

SideSection.propTypes = {
  masterType: PropTypes.string.isRequired,
  masters: PropTypes.array.isRequired,
  actionHandler: PropTypes.func.isRequired,
};

const DefaultCoverageMastersList = ({
  defaultCoverageMasters = [],
  actionHandler,
}) => {
  const bySide = groupByPayerSide(defaultCoverageMasters);

  return (
    <div>
      <Header data-testid='default-coverage-masters-heading'>
        Default Coverage Masters
      </Header>

      <Message info data-testid='default-coverage-masters-help'>
        <Icon name='info circle' />
        A master makes one product the default for one payer side: every
        qualifying sale on that side is charged for it unless that party has
        opted out of that product.
      </Message>

      <SideSection
        masterType={MASTER_TYPE.BUYER}
        masters={bySide[MASTER_TYPE.BUYER]}
        actionHandler={actionHandler}
      />
      <SideSection
        masterType={MASTER_TYPE.SELLER}
        masters={bySide[MASTER_TYPE.SELLER]}
        actionHandler={actionHandler}
      />
      <SideSection
        masterType={MASTER_TYPE.SELLER_PAID}
        masters={bySide[MASTER_TYPE.SELLER_PAID]}
        actionHandler={actionHandler}
      />
    </div>
  );
};

DefaultCoverageMastersList.propTypes = {
  actionHandler: PropTypes.func.isRequired,
  defaultCoverageMasters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      masterType: PropTypes.string,
      productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      productCode: PropTypes.string,
      productName: PropTypes.string,
      version: PropTypes.number,
      startTs: PropTypes.string,
      endTs: PropTypes.string,
      priceDs: PropTypes.number,
      resolving: PropTypes.bool,
      liveButNotResolving: PropTypes.bool,
    })
  ),
};

export default DefaultCoverageMastersList;
